import Honeybadger from "@honeybadger-io/js";
import HoneybadgerVue from "@honeybadger-io/vue";

// Query params that must never leave this app verbatim: the player's session
// token arrives in the launch URL (?token=...&account=...), and anyone with
// Honeybadger dashboard access could replay it. `config.filters` only matches
// object keys, so it does not touch these values when they show up inside a
// URL string (notice.url, cgi_data.HTTP_REFERER) - hence the explicit
// redaction below.
const SENSITIVE_PARAMS = ["token", "otp", "secret", "password"];

export function redactUrl(url?: string): string | undefined {
  if (!url) return url;
  return SENSITIVE_PARAMS.reduce(
    (acc, p) => acc.replace(new RegExp(`([?&]${p}=)[^&#]*`, "gi"), "$1[FILTERED]"),
    url
  );
}

// Browser extensions inject scripts into every page and throw errors that
// have nothing to do with this app; skip reporting those.
Honeybadger.beforeNotify((notice) => {
  if (notice?.stack?.includes("-extension://")) return false;
  if (!notice) return;

  notice.url = redactUrl(notice.url);

  const rawReferer =
    typeof notice.cgiData?.HTTP_REFERER === "string"
      ? notice.cgiData.HTTP_REFERER
      : typeof document !== "undefined"
        ? document.referrer
        : undefined;

  notice.cgiData = { ...notice.cgiData, HTTP_REFERER: redactUrl(rawReferer) };
});

// Which app this is. Exported so main.ts can compose the same prefix onto
// `component` that `tags` below carries, instead of keeping the two in sync by hand.
export const HB_APP = "fe";

export function buildHoneybadgerConfig(env: Record<string, string | undefined>) {
  const apiKey = env.VUE_APP_HONEYBADGER_API_KEY ?? "";

  return {
    apiKey,
    environment: env.VUE_APP_HONEYBADGER_ENV ?? "local",
    // Must match the revision the build uploads source maps under (see vue.config.js), or Honeybadger cannot match a trace to its map.
    revision: env.VUE_APP_REVISION || "main",
    // The SDK's `__developmentMode()` discards every report when `environment`
    // is one of `dev`, `development` or `test`, unless `reportData` is `true`
    // by strict comparison. The dev deployment sets VUE_APP_HONEYBADGER_ENV="dev"
    // and the ticket requires dev tracking - so this must stay a real boolean.
    reportData: apiKey !== "",
    // FE, cms and www report into one Honeybadger project, which has no notion
    // of sub-apps, so this tag is what `tag:fe` filters on. `component`
    // narrows it further and is set per route in main.ts.
    tags: [HB_APP],
    // Replaces the SDK's default list rather than extending it, so every entry
    // already here has to stay. Matching is a case-insensitive substring test,
    // so "token" already covers "accessToken" and "authorization" covers
    // "Authorization".
    filters: ["token", "otp", "secret", "password", "creditcard", "authorization"],
    breadcrumbsEnabled: false,
  };
}

export { Honeybadger, HoneybadgerVue };
